import React, { memo, useEffect, useRef, useState } from 'react'
import { CSSTransition } from 'react-transition-group'
import { CalendarIcon } from '../../assets/icons/CalendarIcon'
import { CloseIcon } from '../../assets/icons/CloseIcon'
import { getUniqueKey } from '../../utils/HTMLutils'
import { validaData } from '../../utils/ValidationUtils'
import IconButton from '../iconButton/IconButton'
import DatePickerCalendar from './components/datePickerCalendar/DatePickerCalendar'
import './DatePicker.scss'
const rootClassName = 'component-date-picker'

interface DatePickerProps {
  initialValue?: string
  onChange?: (evt: any) => void
  queryToAppend: string
  language?: {
    daysOfWeek: string[]
    months: string[]
    monthsAbrev: string[]
  }
  closeOnChange?: boolean
}
function DatePicker(props: DatePickerProps) {
  const [id] = useState(getUniqueKey())
  const [opened, setOpened] = useState(false)

  useEffect(() => {
    const func = (evt: any) => {
      if (evt.path) {
        if (
          document.querySelector(
            `#${id}.${rootClassName}-panel.${rootClassName}-panel-enter-done`
          ) &&
          !evt.path.includes(document.querySelector(`#${id}`))
        )
          setOpened(false)
      } else {
        // FIREFOX
        let aux = evt.target
        let deveFechar = true
        while (aux && aux.id !== 'root') {
          if (
            aux &&
            aux.id &&
            aux.id === id &&
            aux.classList.contains(`${rootClassName}-panel`)
          ) {
            deveFechar = false
            break
          }
          aux = aux.parentElement
        }
        if (deveFechar) {
          setOpened(false)
        }
      }
    }
    const onEsc = (evt: any) => {
      if (evt.key === 'Escape') {
        setOpened(false)
        evt.preventDefault()
      }
    }

    if (opened) {
      document.addEventListener('keydown', onEsc)
      document.addEventListener('click', func)
    } else {
      document.removeEventListener('keydown', onEsc)
      document.removeEventListener('click', func)
    }
    return () => {
      document.removeEventListener('keydown', onEsc)
      document.removeEventListener('click', func)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened])

  const [inter, setInter] = useState<number | null>(null)
  const interRef = useRef(inter)
  interRef.current = inter

  useEffect(() => {
    const current = interRef.current
    if (current) {
      clearInterval(current)
      setInter(null)
    }
    const position = () => {
      const element = document.querySelector(props.queryToAppend) as HTMLElement
      const panel = document.querySelector(
        `#${id}.${rootClassName}-panel`
      ) as HTMLElement

      if (!element || !panel) return
      const elementBound = element.getBoundingClientRect()
      // panel.style.width = `${parseFloat(elementBound.width)}px`

      const windowHeight = parseFloat(String(window.innerHeight))
      const panelHeight = parseFloat(
        String(panel.getBoundingClientRect().height)
      )
      const fitHeight =
        panelHeight + parseFloat(String(elementBound.bottom)) < windowHeight
      panel.style.right = `${
        parseFloat(String(window.innerWidth)) -
        parseFloat(String(elementBound.right))
      }px`
      if (fitHeight) {
        panel.style.top = `${parseFloat(String(elementBound.bottom)) + 2}px`
        panel.style.bottom = 'unset'
        panel.style.borderRadius = '0 0 8px 8px'
      } else {
        panel.style.borderRadius = '8px'
        panel.style.top = `${
          parseFloat(String(elementBound.top)) - panelHeight
        }px`
        panel.style.bottom = 'unset'
      }
    }
    if (opened) {
      position()
      const intervalReturn: any = setInterval(position, 100)
      setInter(intervalReturn)
    }
    return () => {
      if (interRef.current) {
        clearInterval(interRef.current)
        setInter(null)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened])

  const [value, setValue] = useState<string | null>(null)

  useEffect(() => {
    const vd = () => {
      if (props.initialValue && validaData(props.initialValue)) {
        setValue(props.initialValue)
      } else {
        setValue(null)
      }
    }
    vd()
  }, [props.initialValue])

  return (
    <div className={rootClassName}>
      {props.initialValue ? (
        <IconButton
          className={`${rootClassName}-clear-button`}
          icon={<CloseIcon />}
          onClick={() => {
            if (typeof props.onChange === 'function') {
              props.onChange('')
            }
          }}
        />
      ) : undefined}
      <IconButton
        icon={<CalendarIcon />}
        onClick={() => {
          setOpened(!opened)
        }}
      />
      <CSSTransition
        in={opened}
        timeout={300}
        classNames={`${rootClassName}-panel`}
        unmountOnExit
      >
        <div id={id} className={`${rootClassName}-panel`}>
          <DatePickerCalendar
            language={props.language}
            value={value}
            onChange={(date) => {
              setValue(date)
              if (typeof props.onChange === 'function') {
                props.onChange(date)
                if (props.closeOnChange) {
                  setTimeout(() => setOpened(!opened), 250)
                }
              }
            }}
          />
        </div>
      </CSSTransition>
    </div>
  )
}

DatePicker.defaultProps = {
  language: {
    daysOfWeek: ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'],
    months: [
      'Janeiro',
      'Fevereiro',
      'Março',
      'Abril',
      'Maio',
      'Junho',
      'Julho',
      'Agosto',
      'Setembro',
      'Outubro',
      'Novembro',
      'Dezembro'
    ],
    monthsAbrev: [
      'jan',
      'fev',
      'mar',
      'abr',
      'mai',
      'jun',
      'jul',
      'ago',
      'set',
      'out',
      'nov',
      'dez'
    ]
  },
  closeOnChange: false
}

export default memo(DatePicker)
